import { Navigate, type RouteObject } from 'react-router-dom';
import { BlogRoutes, HiddenProductRoutes, ProjectRoutes, Routes } from './routes';

export const routeConfig: RouteObject[] = [
  // Hidden, standalone product landing pages. Top-level (outside the `/` Root
  // layout) so they carry their own header/footer/SEO. See src/business/product-pages.
  {
    path: HiddenProductRoutes.TALENT_MATCHER,
    lazy: () =>
      import('./product-pages/talent-matcher/talent-matcher').then((m) => ({ Component: m.TalentMatcher })),
  },
  {
    path: HiddenProductRoutes.PAY_TRANSPARENCY,
    lazy: () =>
      import('./product-pages/pay-transparency/pay-transparency').then((m) => ({ Component: m.PayTransparency })),
  },
  {
    path: '/',
    lazy: () => import('./root').then((m) => ({ Component: m.Root })),
    children: [
      {
        path: Routes.HOME,
        lazy: () => import('./pages/home/home').then((m) => ({ Component: m.Home })),
      },
      {
        path: Routes.KNOWLEDGE_MANAGEMENT,
        lazy: () => import('./pages/knowledge-management/knowledge-management').then((m) => ({ Component: m.KnowledgeManagement })),
        handle: {
          title: () => 'Sovereign AI Knowledge Management - ZANEFFI',
          description: () => 'Find any answer locked in your documents, systems, and team expertise. Cited, multilingual, and instant. Self-hosted or EU cloud, your data never leaves your control.',
        },
      },
      {
        path: Routes.AI_AGENTS,
        lazy: () => import('./pages/ai-agents/ai-agents').then((m) => ({ Component: m.AIAgents })),
        handle: {
          title: () => 'Sovereign AI Agents - ZANEFFI',
          description: () => 'AI agents that take on real work: qualifying leads, screening candidates, analysing documents, and surfacing what needs attention. Deployed on your infrastructure.',
        },
      },
      {
        path: Routes.AUTOMATION,
        lazy: () => import('./pages/automation/automation').then((m) => ({ Component: m.Automation })),
        handle: {
          title: () => 'Sovereign Intelligent Automation - ZANEFFI',
          description: () => 'Automate the repetitive work across finance, HR, and operations: document processing, data entry, approvals, and dispatch. On your infrastructure, never trained on your data.',
        },
      },
      {
        path: Routes.FIELD_SERVICE_MAINTENANCE,
        lazy: () => import('./pages/field-service-maintenance/field-service-maintenance').then((m) => ({ Component: m.FieldServiceMaintenance })),
        handle: {
          title: () => 'Sovereign AI for Field Service & Industrial Maintenance - ZANEFFI',
          description: () => 'Give every technician instant, cited answers from your documentation and field expertise. Cut mean time to repair and scale know-how across sites, turnarounds, and shifts.',
        },
      },
      // Legacy redirects (old solution slugs -> new capability/solution pages)
      { path: Routes.LEGACY_CRITICAL_INFRASTRUCTURE, element: <Navigate to={Routes.KNOWLEDGE_MANAGEMENT} replace /> },
      { path: Routes.LEGACY_INDUSTRIAL_OPERATIONS, element: <Navigate to={Routes.FIELD_SERVICE_MAINTENANCE} replace /> },
      { path: Routes.LEGACY_ENTERPRISE_AI, element: <Navigate to={Routes.AUTOMATION} replace /> },
      {
        path: Routes.PROJECTS,
        lazy: () => import('./pages/project-list/project-list').then((m) => ({ Component: m.ProjectList })),
        handle: {
          title: () => 'Projects - ZANEFFI',
          // description: () => '',
        },
      },
      {
        path: Routes.BLOG,
        lazy: () => import('./pages/blog-list/blog-list').then((m) => ({ Component: m.BlogList })),
        handle: {
          title: () => 'Sovereign AI Insights - ZANEFFI',
          description: () => 'Expert perspectives on sovereign AI, operational intelligence, and digital transformation for critical infrastructure, manufacturing, and field service operations.',
        },
      },
      // {
      //   path: Routes.OUR_METHOD,
      //   lazy: () => import('./pages/home/home').then((m) => ({ Component: m.Home })),
      //   handle: {
      //     title: () => 'Our - ZANEFFI Method',
      //   },
      // },
      {
        path: Routes.PRIVACY_POLICY,
        lazy: () => import('./pages/privacy/privacy').then((m) => ({ Component: m.Privacy })),
        handle: {
          title: () => 'Privacy Policy - ZANEFFI',
          // description: () => '',
        },
      },
      {
        path: Routes.TERMS_AND_CONDITIONS,
        lazy: () => import('./pages/terms/terms').then((m) => ({ Component: m.Terms })),
        handle: {
          title: () => 'Terms and Conditions - ZANEFFI',
          // description: () => '',
        },
      },
      {
        path: Routes.MEET_WITH_US,
        lazy: () => import('./pages/meet-with-us/meet-with-us').then((m) => ({ Component: m.MeetWithUs })),
        handle: {
          title: () => 'Discuss your project - ZANEFFI',
          // description: () => '',
        },
      },
      {
        path: Routes.CONTACT,
        lazy: () => import('./pages/contact/contact').then((m) => ({ Component: m.Contact })),
        handle: {
          title: () => 'Contact our team - ZANEFFI',
          // description: () => '',
        },
      },
      // Project case studies
      ...Object.values(ProjectRoutes).map((projectId) => ({
        path: `${Routes.PROJECTS}${projectId}`,
        lazy: async () => {
          const [{ CaseStudy }, { PROJECT_DATA }] = await Promise.all([
            import('./pages/case-study/case-study'),
            import('./data-projects'),
          ]);
          return {
            Component: () => <CaseStudy projectId={projectId} />,
            handle: {
              title: () => `${PROJECT_DATA[projectId].title} - ZANEFFI`,
              description: () => PROJECT_DATA[projectId]?.description ?? '',
            },
          };
        },
      })),
      // Blog posts
      ...Object.values(BlogRoutes).map((blogSlug) => ({
        path: `${Routes.BLOG}${blogSlug}`,
        lazy: async () => {
          const [{ BlogPost }, { BLOG_DATA }] = await Promise.all([
            import('./pages/blog-post/blog-post'),
            import('./data-blog'),
          ]);
          return {
            Component: () => <BlogPost blogSlug={blogSlug} />,
            handle: {
              title: () => `${BLOG_DATA[blogSlug].title} - ZANEFFI`,
              description: () => BLOG_DATA[blogSlug].excerpt,
            },
          };
        },
      })),
      {
        path: '*',
        lazy: () => import('./pages/not-found/not-found').then((m) => ({ Component: m.NotFound })),
      },
    ],
  },
];
